import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Home as HomeIcon, Building2, Castle, BedSingle, Minus, Plus, TrendingUp, Moon, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import Reveal from "../components/site/Reveal";
import { HowItWorks, Testimonials } from "../components/site/HomeSections";
import { CITIES, TYPES, estimate } from "../mock";
import { api, errMsg } from "../lib/api";
import ConsentCheck from "../components/site/ConsentCheck";

const TYPE_ICONS = { studio: BedSingle, appartement: Building2, maison: HomeIcon, villa: Castle };
const fmt = (n) => n.toLocaleString("fr-FR") + " €";

function Counter({ label, value, onChange, min = 1, max = 12, testid }) {
  return (
    <div className="flex items-center justify-between rounded-2xl ring-1 ring-[#e6dcc8] p-4">
      <span className="font-semibold text-[#2c3a31]">{label}</span>
      <div className="flex items-center gap-4">
        <button type="button" data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(min, value - 1))} className="w-9 h-9 rounded-full ring-1 ring-[#dccfb6] grid place-items-center hover:bg-[#f1ebdd] disabled:opacity-40" disabled={value <= min}><Minus className="w-4 h-4" /></button>
        <span className="w-6 text-center font-extrabold text-lg" data-testid={`${testid}-value`}>{value}</span>
        <button type="button" data-testid={`${testid}-plus`} onClick={() => onChange(Math.min(max, value + 1))} className="w-9 h-9 rounded-full ring-1 ring-[#dccfb6] grid place-items-center hover:bg-[#f1ebdd] disabled:opacity-40" disabled={value >= max}><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function Proprietaires() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    city: params.get("city") && CITIES.some((c) => c.name === params.get("city")) ? params.get("city") : "Saint-Malo",
    type: params.get("type") || "appartement",
    bedrooms: Number(params.get("bedrooms")) || 2,
    guests: 4, surface: "55", address: "",
    name: "", email: "", phone: "", plan: params.get("plan") || "serenite",
  });
  const [result, setResult] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const preview = useMemo(() => estimate(f), [f]);

  useEffect(() => { if (params.get("city") && params.get("type")) setStep(1); }, []); // eslint-disable-line

  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const next = async () => {
    if (step === 2) {
      if (!f.name.trim()) return toast.error("Merci d'indiquer votre nom");
      if (!/\S+@\S+\.\S+/.test(f.email)) return toast.error("Adresse email invalide");
      if (f.phone.replace(/\D/g, "").length < 10) return toast.error("Numéro de téléphone invalide");
      if (!consent) return toast.error("Merci de cocher la case de consentement");
      const r = estimate(f);
      setSending(true);
      try {
        await api.simulation({ ...f, bedrooms: Number(f.bedrooms), guests: Number(f.guests), annual: r.annual, monthly: r.monthly, nightly: r.nightly, consent: true });
      } catch (e) { setSending(false); return toast.error(errMsg(e)); }
      setSending(false);
      setResult(r);
      toast.success("Votre simulation est prête !");
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setStep(step + 1);
  };

  const max = result ? Math.max(...result.months.map((m) => m.value)) : 1;
  const STEPS_LBL = ["Votre bien", "Caractéristiques", "Vos coordonnées"];

  return (
    <div data-testid="proprietaires-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#d9895a]/15 blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-24 grid lg:grid-cols-12 gap-12 relative">
          <div className="lg:col-span-5">
            <Reveal><span className="text-sm font-bold uppercase tracking-[0.2em] text-[#c4703f]">Propriétaires</span></Reveal>
            <Reveal delay={80}><h1 className="font-display mt-4 text-5xl lg:text-[64px] font-semibold text-[#13261c] leading-[1.03]">Découvrez le potentiel de votre logement</h1></Reveal>
            <Reveal delay={160}><p className="mt-6 text-lg text-[#4a5249] leading-relaxed">Notre algorithme analyse plus de 4 000 annonces de la Côte d'Émeraude et vous donne un aperçu indicatif de ce que votre bien pourrait rapporter en location courte durée.</p></Reveal>
            <Reveal delay={240} className="mt-10 relative rounded-[32px] overflow-hidden aspect-[4/3] hidden lg:block">
              <img src="/photos/salon-lumineux.jpg" alt="Salon lumineux d'un logement en location saisonnière" className="w-full h-full object-cover object-[40%_center]" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-[#f1ebdd] grid place-items-center"><TrendingUp className="w-5 h-5 text-[#c4703f]" /></span>
                <p className="text-sm text-[#2c3a31]"><strong className="text-[#13261c]">+ 32 %</strong> de revenus en moyenne vs location en direct</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={120} className="bg-white rounded-[36px] p-6 sm:p-10 shadow-[0_40px_90px_-40px_rgba(19,38,28,0.5)] ring-1 ring-[#13261c]/5">
              {step < 3 ? (
                <div data-testid="estimation-form">
                  <div className="flex justify-between text-sm font-bold text-[#6d7268]"><span>Étape {step + 1} / 3 · {STEPS_LBL[step]}</span><span className="text-[#c4703f]">≈ {fmt(preview.monthly)}/mois</span></div>
                  <Progress value={((step + 1) / 3) * 100} className="mt-3 h-2 bg-[#f1ebdd] [&>div]:bg-[#13261c]" />

                  {step === 0 && (
                    <div className="mt-8 space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                      <div>
                        <p className="font-bold text-[#13261c] mb-3">Où se situe votre logement ?</p>
                        <Select value={f.city} onValueChange={(v) => set("city", v)}>
                          <SelectTrigger className="h-14 rounded-2xl text-base" data-testid="form-city"><SelectValue /></SelectTrigger>
                          <SelectContent>{CITIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Adresse (facultatif)" className="h-14 rounded-2xl mt-3 text-base" data-testid="form-address" />
                      </div>
                      <div>
                        <p className="font-bold text-[#13261c] mb-3">Type de bien</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {TYPES.map((t) => {
                            const I = TYPE_ICONS[t.id];
                            const on = f.type === t.id;
                            return (
                              <button type="button" key={t.id} onClick={() => set("type", t.id)} data-testid={`form-type-${t.id}`}
                                className={`rounded-2xl p-5 flex flex-col items-center gap-2 font-bold transition-colors ring-1 ${on ? "bg-[#13261c] text-white ring-[#13261c]" : "ring-[#e6dcc8] text-[#2c3a31] hover:bg-[#f1ebdd]"}`}>
                                <I className="w-7 h-7" />{t.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="mt-8 space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                      <p className="font-bold text-[#13261c]">Parlez-nous de votre logement</p>
                      <Counter label="Chambres" value={f.bedrooms} onChange={(v) => set("bedrooms", v)} max={8} testid="form-bedrooms" />
                      <Counter label="Voyageurs max." value={f.guests} onChange={(v) => set("guests", v)} max={16} testid="form-guests" />
                      <div className="flex items-center justify-between rounded-2xl ring-1 ring-[#e6dcc8] p-4">
                        <span className="font-semibold text-[#2c3a31]">Surface (m²)</span>
                        <Input type="number" value={f.surface} onChange={(e) => set("surface", e.target.value)} className="w-28 h-10 rounded-xl text-right" data-testid="form-surface" />
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="mt-8 space-y-3 animate-in fade-in slide-in-from-right-4 duration-300">
                      <p className="font-bold text-[#13261c]">Où vous envoyer votre simulation détaillée ?</p>
                      <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Nom et prénom" className="h-14 rounded-2xl text-base" data-testid="form-name" />
                      <Input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" type="email" className="h-14 rounded-2xl text-base" data-testid="form-email" />
                      <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Téléphone" className="h-14 rounded-2xl text-base" data-testid="form-phone" />
                      <ConsentCheck checked={consent} onChange={setConsent} testid="form-consent" />
                    </div>
                  )}

                  <div className="mt-10 flex justify-between gap-3">
                    <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="form-back"
                      className="inline-flex items-center gap-2 rounded-full px-6 py-4 font-bold text-[#2c3a31] hover:bg-[#f1ebdd] disabled:opacity-0 transition-colors"><ArrowLeft className="w-5 h-5" />Retour</button>
                    <button type="button" onClick={next} disabled={sending} data-testid="form-next"
                      className="group inline-flex items-center gap-2 rounded-full bg-[#13261c] hover:bg-[#2a4636] text-white px-8 py-4 font-bold transition-colors">
                      {sending ? "Envoi…" : step === 2 ? "Voir ma simulation" : "Continuer"}<ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>
              ) : (
                <div data-testid="estimation-result" className="animate-in fade-in zoom-in-95 duration-500">
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#f1ebdd] text-[#c4703f] px-4 py-2 text-sm font-bold"><Check className="w-4 h-4" />Simulation pour {f.name.split(" ")[0]}</span>
                  <p className="mt-6 text-[#6d7268] font-semibold">{TYPES.find((t) => t.id === f.type)?.label} · {f.bedrooms} ch. · {f.city}</p>
                  <p className="font-display text-6xl lg:text-7xl font-semibold text-[#13261c] mt-2" data-testid="result-annual">{fmt(result.annual)}</p>
                  <p className="text-[#5a6158] font-medium">de revenus bruts potentiels par an (simulation indicative)</p>
                  <div className="mt-6 grid grid-cols-3 gap-3">
                    {[[CalendarDays, "Par mois", fmt(result.monthly)], [Moon, "Prix / nuit", fmt(result.nightly)], [TrendingUp, "Occupation", "68 %"]].map(([I, k, v]) => (
                      <div key={k} className="rounded-2xl bg-[#f3ede1] p-4"><I className="w-5 h-5 text-[#c4703f]" /><p className="text-xs text-[#6d7268] font-semibold mt-2">{k}</p><p className="font-extrabold text-[#13261c]">{v}</p></div>
                    ))}
                  </div>
                  <div className="mt-8 h-40 flex items-end gap-1.5">
                    {result.months.map((m) => (
                      <div key={m.m} className="flex-1 flex flex-col items-center gap-1.5 group">
                        <div className="w-full rounded-t-md bg-[#e6dcc8] group-hover:bg-[#13261c] transition-colors relative" style={{ height: `${(m.value / max) * 130}px` }}>
                          <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-[10px] font-bold bg-white shadow px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">{fmt(m.value)}</span>
                        </div>
                        <span className="text-[10px] font-semibold text-[#6d7268]">{m.m}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-6 text-sm text-[#6d7268]">Un conseiller vous contactera sous 24h à {f.email} pour en discuter lors d'un rendez-vous gratuit.</p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/rendez-vous" className="rounded-full bg-[#13261c] hover:bg-[#2a4636] text-white px-7 py-4 font-bold transition-colors" data-testid="result-contact">Prendre rendez-vous</Link>
                    <button onClick={() => { setStep(0); setResult(null); }} className="rounded-full ring-1 ring-[#dccfb6] px-7 py-4 font-bold text-[#13261c] hover:bg-[#f1ebdd] transition-colors" data-testid="result-restart">Nouvelle simulation</button>
                  </div>
                </div>
              )}
            </Reveal>
          </div>
        </div>
      </section>
      <HowItWorks />
      <Testimonials />
    </div>
  );
}
